import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Card from "../Card";
import { Checkbox } from "../../atoms/CheckBox";
import "../DeleteChatPopup/index.css";

const DeleteMessagePopup = ({
    message,
    allowDeleteForEveryone = true,
    onDeleteForEveryone,
    onDeleteForMe,
    closeModal,
}) => {
    const { t } = useTranslation();
    const [confirmDeleteForEveryone, setConfirmDeleteForEveryone] = useState(Boolean(allowDeleteForEveryone));
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const canDeleteForMe = typeof onDeleteForMe === "function";
    const canChooseDeleteMode = allowDeleteForEveryone && canDeleteForMe;

    useEffect(() => {
        setConfirmDeleteForEveryone(Boolean(allowDeleteForEveryone));
        setDeleteError(null);
    }, [allowDeleteForEveryone, canDeleteForMe, message?.id]);

    const handleDelete = async () => {
        if (!message?.id) {
            return;
        }

        setIsDeleting(true);
        setDeleteError(null);
        try {
            const shouldDeleteForEveryone = allowDeleteForEveryone && (!canDeleteForMe || confirmDeleteForEveryone);
            if (shouldDeleteForEveryone) {
                if (typeof onDeleteForEveryone !== "function") {
                    throw new Error("Delete for everyone is not available");
                }
                await onDeleteForEveryone(message);
            } else if (canDeleteForMe) {
                await onDeleteForMe(message);
            } else {
                throw new Error("Delete for me is not available");
            }
            closeModal?.();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Failed to delete message", err);
            setDeleteError(t("chat.deleteMessage.error"));
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Card
            id="deleteMessagePopup"
            title={
                <>
                    <span className="delete-chat-popup__eyebrow">{t("chat.deleteMessage.eyebrow")}</span>
                    <span className="delete-chat-popup__title">{t("chat.deleteMessage.title")}</span>
                </>
            }
            body={
                <>
                    <p className="delete-chat-popup__prompt">{t("chat.deleteMessage.prompt")}</p>
                    {canChooseDeleteMode && (
                        <Checkbox
                            isChecked={confirmDeleteForEveryone}
                            onChange={(e) => setConfirmDeleteForEveryone(e.target.checked)}
                            label={t("chat.deleteMessage.deleteForEveryone")}
                        />
                    )}
                    {deleteError && (
                        <p className="delete-chat-popup__error" role="alert">
                            {deleteError}
                        </p>
                    )}
                </>
            }
            footer={
                <div className="delete-chat-popup__actions">
                    <button
                        type="button"
                        className="delete-chat-popup__cancel"
                        onClick={closeModal}
                        disabled={isDeleting}
                    >
                        {t("chat.deleteMessage.cancel")}
                    </button>
                    <button
                        type="button"
                        className="delete-chat-popup__confirm"
                        onClick={handleDelete}
                        disabled={isDeleting || !message?.id}
                    >
                        {isDeleting ? t("chat.deleteMessage.deleting") : t("chat.deleteMessage.confirm")}
                    </button>
                </div>
            }
        />
    );
};

DeleteMessagePopup.propTypes = {
    message: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        senderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        chatId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    allowDeleteForEveryone: PropTypes.bool,
    onDeleteForEveryone: PropTypes.func,
    onDeleteForMe: PropTypes.func,
    closeModal: PropTypes.func,
};

export default DeleteMessagePopup;
